import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Bot,
  Package,
  ShoppingCart,
  FileText,
  Calendar,
  Wallet,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  Zap,
  Crown,
  Sparkles,
  Bell,
  MessagesSquare,
  FolderOpen
} from 'lucide-react';
import { useSidebar } from '../../contexts/SidebarContext';
import { useOrdersRealtime } from '../../contexts/OrdersRealtimeContext';
import { useLang } from '../../i18n/LanguageContext';
import CreditBadge from '../Credits/CreditBadge';
import useSidebarCounts from '../../hooks/useSidebarCounts';
import logo from '../../assets/logo.svg';
import logoLight from '../../assets/logo-light.svg';
import emblemLogo from '../../assets/EMBLEME LOGO.png';
import { useTheme } from '../../contexts/ThemeContext';

function WhatsAppIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M12 2C6.477 2 2 6.477 2 12c0 1.89.525 3.66 1.438 5.168L2 22l4.978-1.41A9.955 9.955 0 0012 22c5.523 0 10-4.477 10-10S17.523 2 12 2z"
        fill="currentColor" fillOpacity="0.18"
        stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round"
      />
      <path
        d="M8.5 9.5c.167-.5.5-1 1-1.2.4-.16.7-.1.9.1l.8 1.1c.2.25.15.55-.05.75l-.5.5c-.1.1-.1.25 0 .35.3.45.75.95 1.2 1.3.45.35.9.65 1.3.8.15.05.3 0 .4-.1l.5-.5c.2-.2.5-.25.75-.05l1.1.8c.2.2.25.5.1.9-.2.5-.7.85-1.2 1-.9.25-2.55-.1-4.2-1.75C8.6 12.05 8.25 10.4 8.5 9.5z"
        fill="currentColor"
      />
    </svg>
  );
}

// menuItems est construit dynamiquement à partir des traductions dans le composant
const MENU_ITEMS_STATIC = [
  { key: 'dashboard',       icon: Home,          path: '/',                  countKey: null },
  { key: 'agents',          icon: Bot,           path: '/agents',            countKey: 'agents' },
  { key: 'produits',        icon: Package,       path: '/produits',          countKey: 'produits' },
  { key: 'whatsapp',        icon: WhatsAppIcon,  path: '/whatsapp',          countKey: 'conversations' },
  { key: 'ragChat',         icon: MessagesSquare,path: '/rag/chat',          countKey: null },
  { key: 'ragDocs',         icon: FolderOpen,    path: '/rag/documents',     countKey: null },
  { key: 'commandes',       icon: ShoppingCart,  path: '/commandes',         countKey: 'commandes' },
  { key: 'caisse',          icon: Wallet,        path: '/caisse',            countKey: null },
  { key: 'factures',        icon: FileText,      path: '/factures',          countKey: null },
  { key: 'relances',        icon: Bell,          path: '/relances',          countKey: null },
  { key: 'calendrier',      icon: Calendar,      path: '/calendrier',        countKey: null },
  { key: 'parametres',      icon: Settings,      path: '/settings',          countKey: null },
];

export default function ModernSidebar() {
  const location = useLocation();
  const navigate = useNavigate();
  const sidebarRef = useRef(null);
  const timeoutRef = useRef(null);
  const { t } = useLang();
  const { resolvedTheme } = useTheme();
  const activeLogo = resolvedTheme === 'light' ? logoLight : logo;
  const {
    isCollapsed,
    setIsCollapsed,
    isHovered,
    setIsHovered,
    isMobileMenuOpen,
    setIsMobileMenuOpen,
    isExpanded
  } = useSidebar();

  const menuItems = MENU_ITEMS_STATIC.map((item) => ({
    ...item,
    name: t.sidebar.menu[item.key].name,
    description: t.sidebar.menu[item.key].description,
  }));
  
  const [isMobile, setIsMobile] = useState(false);
  const [isKeyboardNav, setIsKeyboardNav] = useState(false);
  const [user, setUser] = useState(null);

  // Compteurs réels affichés dans les badges (agents, commandes, etc.)
  const { counts, refetch: refetchCounts } = useSidebarCounts();

  // Compteur temps réel des nouvelles commandes non vues (badge rouge)
  const { unseenCount: newOrdersCount } = useOrdersRealtime();

  // Rafraîchir les compteurs à chaque changement de page :
  // après avoir créé/supprimé un agent, un produit, etc., le badge reste à jour.
  useEffect(() => {
    refetchCounts();
  }, [location.pathname, refetchCounts]);

  // Détection mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Chargement utilisateur
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  // Auto-rétractation après inactivité
  useEffect(() => {
    if (!isMobile && !isCollapsed && !isHovered) {
      timeoutRef.current = setTimeout(() => {
        setIsCollapsed(true);
      }, 5000); // 5 secondes d'inactivité
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isCollapsed, isHovered, isMobile, setIsCollapsed]);

  // Navigation clavier
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !isMobile) {
        setIsCollapsed(true);
      }
      if (e.key === 'Tab') {
        setIsKeyboardNav(true);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isMobile, setIsCollapsed]);

  const handleMouseEnter = () => {
    if (!isMobile) {
      setIsHovered(true);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setIsHovered(false);
      setIsKeyboardNav(false);
    }
  };

  const handleClick = () => {
    if (!isMobile) {
      setIsCollapsed(!isCollapsed);
    }
  };

  const handleUpgradePro = () => {
    navigate('/settings?tab=subscription');
  };

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const handleMenuItemClick = (path) => {
    navigate(path);
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <>
      {/* Overlay mobile */}
      {isMobile && isMobileMenuOpen && (
        <div
          className="mobile-menu-overlay fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside
        ref={sidebarRef}
        className={`
          modern-sidebar fixed left-0 top-0 h-screen z-50 flex-shrink-0
          transition-all duration-300 ease-in-out
          ${isMobile 
            ? isMobileMenuOpen 
              ? 'translate-x-0 w-72 open' 
              : '-translate-x-full w-72'
            : isExpanded 
              ? 'w-72' 
              : 'w-20'
          }
          ${isKeyboardNav ? 'ring-2 ring-accent-green ring-offset-2 ring-offset-primary-card' : ''}
        `}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onFocus={() => setIsKeyboardNav(true)}
        onBlur={() => setIsKeyboardNav(false)}
        role="navigation"
        aria-label={t.sidebar.ariaLabel}
        aria-expanded={isExpanded}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div
            className={`flex items-center justify-between ${isExpanded ? 'p-4' : 'p-3'}`}
            style={{ borderBottom: '0.5px solid var(--c-glass-border)' }}
          >
            {/* Logo + badge version */}
            <div className={`flex items-center min-w-0 ${isExpanded ? 'gap-2.5' : 'justify-center'}`}>
              {isExpanded ? (
                <div className="flex items-center gap-2 min-w-0">
                  <img
                    src={activeLogo}
                    alt="Swift AI Logo"
                    className="h-7 w-auto flex-shrink-0 logo-transition"
                  />
                  <span
                    className="text-label flex-shrink-0"
                    style={{ fontSize: '10px', color: 'var(--c-text-muted)' }}
                  >
                    v2.1
                  </span>
                </div>
              ) : (
                <img
                  src={emblemLogo}
                  alt="Swift AI"
                  className="logo-emblem flex-shrink-0"
                />
              )}
            </div>

            {/* Boutons de contrôle */}
            <div className="flex items-center gap-2">
              {isMobile ? (
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 rounded-lg hover:bg-primary-dark/50 text-ink transition-colors"
                  aria-label={t.sidebar.close}
                >
                  <X size={20} />
                </button>
              ) : (
                <button
                  onClick={handleClick}
                  className="p-2 rounded-lg hover:bg-primary-dark/50 text-ink transition-all hover:rotate-180"
                  aria-label={isCollapsed ? t.sidebar.expand : t.sidebar.collapse}
                  aria-expanded={!isCollapsed}
                >
                  <ChevronLeft 
                    size={20} 
                    className={`transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`}
                  />
                </button>
              )}
            </div>
          </div>

          {/* Badge de solde de crédits — toujours visible, cliquable vers l'historique */}
          <div className={isExpanded ? 'px-3 pt-3' : 'px-2 pt-3 flex justify-center'}>
            <CreditBadge expanded={isExpanded} />
          </div>

          {/* Navigation principale */}
          <nav className="flex-1 overflow-y-auto p-2.5" role="navigation" aria-label={t.sidebar.ariaLabel}>
            <ul className="space-y-2">
              {menuItems.map((item, index) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                const count = item.countKey ? counts[item.countKey] : 0;
                // Badge rouge prioritaire : nouvelles commandes reçues en temps réel
                const isUrgentOrders = item.path === '/commandes' && newOrdersCount > 0;
                const rawBadge = isUrgentOrders ? newOrdersCount : count;
                const badge = rawBadge > 0 ? (rawBadge > 99 ? '99+' : String(rawBadge)) : null;

                return (
                  <li key={item.path}>
                    <Link
                      to={item.path}
                      onClick={() => handleMenuItemClick(item.path)}
                      data-tour={item.key}
                      className={`
                        menu-item w-full flex items-center gap-3 px-3 py-3
                        transition-all duration-200 ease-in-out
                        group relative overflow-hidden
                        ${isActive 
                          ? 'active'
                          : 'text-text-gray hover:text-ink'
                        }
                        ${isKeyboardNav && isActive ? 'ring-2 ring-accent-green ring-offset-2' : ''}
                      `}
                      aria-label={item.name}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      {/* Icône */}
                      <div className={`
                        relative flex-shrink-0 transition-all duration-200
                        ${isActive ? 'text-accent-green' : 'text-text-gray group-hover:text-ink'}
                      `}>
                        <Icon size={20} />
                        {/* Pastille rouge en mode rétracté pour les nouvelles commandes */}
                        {!isExpanded && isUrgentOrders && (
                          <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center ring-2 ring-primary-card animate-pulse">
                            {newOrdersCount > 9 ? '9+' : newOrdersCount}
                          </span>
                        )}
                      </div>

                      {/* Contenu */}
                      {isExpanded && (
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <span className="font-medium truncate">
                              {item.name}
                            </span>
                            {badge && (
                              <span
                                className={
                                  isUrgentOrders
                                    ? 'min-w-[20px] h-5 px-1.5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse'
                                    : 'menu-badge'
                                }
                              >
                                {badge}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-text-gray/70 truncate mt-0.5">
                            {item.description}
                          </p>
                        </div>
                      )}

                      {/* Indicateur actif (mode rétracté) */}
                      {!isExpanded && isActive && (
                        <div className="absolute right-2 w-1 h-6 bg-accent-green rounded-full" />
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Footer - User info */}
          {isExpanded && user && (
            <div className="p-4 border-t border-primary-dark/20">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 user-avatar rounded-full flex items-center justify-center">
                  <span className="text-accent-green font-bold">
                    {user.name?.charAt(0) || user.username?.charAt(0) || 'U'}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-ink font-medium truncate">
                    {user.name || user.username || t.sidebar.defaultUser}
                  </p>
                  <p className="text-text-gray text-sm truncate">
                    {user.email || 'user@example.com'}
                  </p>
                </div>
              </div>
              
              {/* Bouton Upgrade — gradient vert Swift AI (seul gradient autorisé) */}
              <button
                onClick={handleUpgradePro}
                className="pro-upgrade-btn w-full flex items-center justify-center gap-2 px-4 py-2.5"
              >
                <Crown size={14} style={{ color: '#fff' }} />
                <span style={{ fontSize: '13px', fontWeight: 500, color: '#fff' }}>
                  {t.sidebar.upgradePro}
                </span>
                <Zap size={12} style={{ color: 'rgba(255,255,255,0.7)' }} />
              </button>

              {/* Bouton déconnexion discret */}
              <button
                onClick={handleLogout}
                className="w-full flex items-center justify-center gap-2 px-3 py-2 mt-2 rounded-lg
                  text-gray-400 hover:text-red-400 hover:bg-red-500/5
                  transition-colors duration-200 text-xs"
                aria-label={t.sidebar.logout}
              >
                <LogOut size={14} />
                <span>{t.sidebar.logout}</span>
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* Bouton hamburger mobile */}
      {isMobile && (
        <button
          onClick={() => setIsMobileMenuOpen(true)}
          className="fixed top-4 left-4 z-30 p-3 bg-primary-card rounded-lg shadow-lg
            hover:bg-primary-dark/50 transition-colors duration-200 lg:hidden"
          aria-label={t.sidebar.open}
        >
          <Menu size={24} className="text-ink" />
        </button>
      )}
    </>
  );
}
